'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import FunnelLayout from '@/app/ui/blocks/funnel-layout';
import HeroImage from '@/app/ui/blocks/hero-image';
import TeamLogo from '@/app/ui/blocks/team-logo';
import { DEFAULT_LOCALE, LOCALES, PAGES } from '@/lib/config';
import selectorContent from '@/lib/content/selector.json';
import { toQuery } from '@/lib/helpers';
import { ALL_TEAMS, type Team } from '@/lib/predictor';
import { resolveContent } from '@/lib/predictor-content';

type SelectorLocaleContent = (typeof selectorContent)['en'];

function resolveSelectorCopy(locale: Locale): SelectorLocaleContent {
  const content = selectorContent as Record<string, Partial<SelectorLocaleContent>>;
  return { ...selectorContent[DEFAULT_LOCALE], ...content[locale] };
}

type Props = { locale: Locale };

export default function TeamSelectionPage({ locale }: Props) {
  const router = useRouter();
  const params = useSearchParams();
  const [selectedShort, setSelectedShort] = useState<string | null>(null);

  const content = useMemo(() => resolveContent(locale), [locale]);
  const copy = useMemo(() => resolveSelectorCopy(locale), [locale]);

  const sortedTeams = useMemo(
    () =>
      [...ALL_TEAMS].sort((a, b) => content.teams[a.short].localeCompare(content.teams[b.short])),
    [content],
  );

  function handleConfirm() {
    if (!selectedShort) return;
    const query = { ...Object.fromEntries(params.entries()), team: selectedShort };
    router.push(`/${locale}${PAGES.PREDICTOR}${toQuery(query)}`);
  }

  return (
    <FunnelLayout>
      <HeroImage
        desktop={copy.heroImage.desktop}
        mobile={copy.heroImage.mobile}
        alt={copy.heroImage.alt}
      >
        <LocaleSwitcher locale={locale} />
        {/* H1 overlay temporarily disabled — the hero image already contains
            the "Predict and win" creative; uncomment if marketing wants the
            text back on top of the image.
          <div className="relative z-[1] flex h-full max-w-full items-center justify-center px-5 text-center">
            <h1 className="fade-up font-termina text-[40px] leading-[1] font-extrabold tracking-[-1.2px] text-white uppercase drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)] sm:text-[56px] md:text-[56px]">
              {copy.hero.titleLines.map((line, idx) => (
                <span key={idx}>
                  {line}
                  {idx < copy.hero.titleLines.length - 1 ? <br /> : ' '}
                </span>
              ))}
              <span className="text-accent">{copy.hero.titleAccent}</span>
            </h1>
          </div>
        */}
      </HeroImage>

      <div className="px-5 pt-6 pb-2 text-center">
        <p className="fade-up font-termina text-[11px] font-normal text-fg-muted opacity-30">
          {copy.hero.subtitle}
        </p>
      </div>

      <div className="mx-auto grid w-[90%] max-w-[620px] grid-cols-2 gap-4 pb-5 md:grid-cols-3 md:gap-5">
        {sortedTeams.map((team, idx) => (
          <TeamCard
            key={team.short}
            team={team}
            name={content.teams[team.short]}
            selected={team.short === selectedShort}
            onSelect={() => setSelectedShort(team.short)}
            delayClass={`fade-up-${(idx % 6) + 1}`}
          />
        ))}
      </div>

      {/* Spacer so the last row of cards isn't covered by the sticky CTA */}
      <div aria-hidden className={selectedShort ? 'h-24' : 'h-0'} />

      <div
        className={`fixed inset-x-0 bottom-0 z-30 border-t border-border-default bg-bg/95 px-5 py-4 backdrop-blur-md transition-transform duration-200 ease-out ${
          selectedShort ? 'translate-y-0' : 'pointer-events-none translate-y-full'
        }`}
      >
        <button
          type="button"
          disabled={!selectedShort}
          onClick={handleConfirm}
          className="mx-auto block w-full max-w-[300px] cursor-pointer border-none bg-accent px-4 py-3.5 font-termina text-[12px] font-extrabold tracking-[1.5px] text-black uppercase transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-bg-surface-2 disabled:text-fg-muted"
        >
          {copy.confirmCta}
        </button>
      </div>
    </FunnelLayout>
  );
}

function LocaleSwitcher({ locale }: { locale: Locale }) {
  const router = useRouter();
  const params = useSearchParams();
  const [open, setOpen] = useState(false);

  function switchTo(code: string) {
    const next = Object.fromEntries(params.entries());
    delete next.locale;
    router.push(`/${code}${PAGES.TEAM_SELECTION}${toQuery(next)}`);
    setOpen(false);
  }

  return (
    <div className="absolute top-4 right-4 z-20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex cursor-pointer items-center gap-1 border border-white bg-black/50 px-3 py-1.5 text-[10px] font-semibold tracking-[1px] text-white uppercase backdrop-blur-[4px] transition-colors hover:bg-black/70"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {locale}
        <span
          aria-hidden
          className={`inline-block transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          &#9662;
        </span>
      </button>
      <ul
        role="listbox"
        className={`absolute right-0 mt-1 min-w-[120px] origin-top-right border border-white bg-black/90 backdrop-blur-[4px] transition-all duration-200 ease-out ${
          open ?
            'pointer-events-auto translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none -translate-y-1 scale-95 opacity-0'
        }`}
      >
        {LOCALES.map((l) => (
          <li key={l.code}>
            <button
              type="button"
              role="option"
              aria-selected={l.code === locale}
              tabIndex={open ? 0 : -1}
              onClick={() => switchTo(l.code)}
              className={`block w-full cursor-pointer px-3 py-2 text-left text-[10px] font-semibold tracking-[1px] uppercase transition-colors hover:bg-white/10 ${l.code === locale ? 'text-accent' : 'text-white'}`}
            >
              {l.code} — {l.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

type TeamCardProps = {
  team: Team;
  name: string;
  selected: boolean;
  onSelect: () => void;
  delayClass: string;
};

function TeamCard({ team, name, selected, onSelect, delayClass }: TeamCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-short={team.short}
      className={`fade-up ${delayClass} flex aspect-[6/4.8] w-full cursor-pointer flex-col items-center justify-center border p-1 text-center transition-colors ${
        selected ?
          'border-white bg-white text-black'
        : 'border-border-default bg-bg-surface text-fg-primary hover:border-accent'
      }`}
    >
      <div className="mb-2 h-[40%] w-[40%]">
        <TeamLogo
          short={team.short}
          variant={selected ? 'colour' : 'mono'}
          size={80}
          className="h-full w-full"
          alt={name}
        />
      </div>
      <div
        className={`font-termina text-[12px] font-extrabold tracking-[0.5px] uppercase md:text-[13px] ${selected ? 'text-black' : ''}`}
      >
        {name}
      </div>
    </button>
  );
}
